import React, { useState, useEffect } from "react";
import api from "./api/axios";
import { useNavigate } from "react-router-dom";
import { useActivityLogger } from "./hooks/useActivity";
import { useAuth } from "./contexts/AuthContext";

export default function ForumCategory() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { logUserActivity, loading: userLoading } = useActivityLogger();
  const { user, loading: loadingUser } = useAuth();

  const categoryColors = [
    "bg-yellow-100",
    "bg-blue-100",
    "bg-blue-50",
    "bg-green-100",
    "bg-purple-100",
    "bg-pink-100",
  ];

  // Helper function to filter categories based on user preferences
  const filterCategoriesByPreference = (categories, user) => {
    if (!user || !user.preferences) {
      // If user is not logged in or has no preferences, filter out all religious categories
      const religiousKeywords = [
        "agama kristen",
        "agama islam",
        "dukungan agama",
        "keagamaan",
        "agama katolik",
        "agama hindu",
        "agama buddha",
        "agama konghucu",
      ];

      return categories.filter((category) => {
        const categoryTitle = category.title?.toLowerCase() || "";
        return !religiousKeywords.some((keyword) =>
          categoryTitle.includes(keyword)
        );
      });
    }

    // Check user's religious preferences
    const userPreferences = user.preferences || [];
    const hasChristianPreference = userPreferences.some(
      (pref) =>
        pref.toLowerCase().includes("keagamaan kristen") ||
        pref.toLowerCase().includes("agama kristen") ||
        pref.toLowerCase().includes("dukungan agama kristen")
    );
    const hasIslamicPreference = userPreferences.some(
      (pref) =>
        pref.toLowerCase().includes("keagamaan islam") ||
        pref.toLowerCase().includes("agama islam") ||
        pref.toLowerCase().includes("dukungan agama islam")
    );
    const hasMedicalInfoPreference = userPreferences.some(
      (pref) =>
        pref.toLowerCase().includes("informasi medis") ||
        pref.toLowerCase().includes("berbagi pengalaman")
    );
    const hasMentalHealthPreference = userPreferences.some(
      (pref) =>
        pref.toLowerCase().includes("kesehatan mental") ||
        pref.toLowerCase().includes("dukungan emosional")
    );
    const hasOtherReligiousPreference = userPreferences.some(
      (pref) =>
        (pref.toLowerCase().includes("keagamaan") ||
          pref.toLowerCase().includes("kepercayaan"))
    );
    const hasCatholicPreference = userPreferences.some(
      (pref) =>
        pref.toLowerCase().includes("agama katolik") ||
        pref.toLowerCase().includes("keagamaan katolik")
    );
    const hasHinduPreference = userPreferences.some(
      (pref) =>
        pref.toLowerCase().includes("agama hindu") ||
        pref.toLowerCase().includes("keagamaan hindu")
    );
    const hasBuddhaPreference = userPreferences.some(
      (pref) =>
        pref.toLowerCase().includes("agama buddha") ||
        pref.toLowerCase().includes("keagamaan buddha")
    );
    const hasKonghucuPreference = userPreferences.some(
      (pref) =>
        pref.toLowerCase().includes("agama konghucu") ||
        pref.toLowerCase().includes("keagamaan konghucu")
    );


    return categories.filter((category) => {
      const categoryTitle = category.title?.toLowerCase() || "";

      // Always show non-religious categories
      // if (!categoryTitle.includes("agama") && !categoryTitle.includes("keagamaan")) {
      //   return true;
      // }

      // For religious categories, check user's preferences
      if (
        categoryTitle.includes("agama kristen") ||
        categoryTitle.includes("keagamaan kristen")
      ) {
        return hasChristianPreference;
      } else if (
        categoryTitle.includes("agama islam") ||
        categoryTitle.includes("keagamaan islam")
      ) {
        return hasIslamicPreference;
      } else if (
        categoryTitle.includes("informasi medis") ||
        categoryTitle.includes("berbagi pengalaman")
      ) {
        return hasMedicalInfoPreference;
      } else if (
        categoryTitle.includes("kesehatan mental") ||
        categoryTitle.includes("dukungan emosional")
      ) {
        return hasMentalHealthPreference;
      } else if (
        categoryTitle.includes("agama katolik") ||
        categoryTitle.includes("keagamaan katolik")
      ) {
        return hasCatholicPreference;
      } else if (
        categoryTitle.includes("agama hindu") ||
        categoryTitle.includes("keagamaan hindu")
      ) {
        return hasHinduPreference;
      } else if (
        categoryTitle.includes("agama buddha") ||
        categoryTitle.includes("keagamaan buddha")
      ) {
        return hasBuddhaPreference;
      } else if (
        categoryTitle.includes("agama konghucu") ||
        categoryTitle.includes("keagamaan konghucu")
      ) {
        return hasKonghucuPreference;
      } else if (
        categoryTitle.includes("keagamaan") ||
        categoryTitle.includes("agama") ||
        categoryTitle.includes("kepercayaan")
      ) {
        return hasOtherReligiousPreference;
      }

      // Hide other religious categories if user doesn't have that preference
      return false;
    });
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await api.get("/forum/categories");

        let categoriesWithColors = response.data.map((category, index) => ({
          id: category.id,
          title: category.name,
          bg: categoryColors[index % categoryColors.length],
        }));

        // Filter categories based on user login status and preferences
        categoriesWithColors = filterCategoriesByPreference(
          categoriesWithColors,
          user
        );

        // console.log("User preferences:", user?.preferences || "No preferences");
        // console.log("Fetched and filtered categories:", categoriesWithColors);

        setCategories(categoriesWithColors);

        if (!userLoading) {
          const religionPrefs =
            user?.preferences?.filter(
              (pref) =>
                pref.toLowerCase().includes("keagamaan") ||
                pref.toLowerCase().includes("agama")
            ) || [];

          logUserActivity(
            "user",
            "view_forum_categories",
            "ForumCategory",
            `User viewed forum categories (logged in: ${!!user}, religious preferences: ${religionPrefs.join(", ") || "none"})`
          );
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setError("Failed to load categories");
        if (!userLoading) {
          logUserActivity(
            "error",
            "fetch_categories_error",
            "ForumCategory",
            `Error fetching categories: ${err.message}`
          );
        }
      } finally {
        setLoading(false);
      }
    };

    // Only fetch when user loading is complete
    if (!loadingUser) {
      fetchCategories();
    }
  }, [user, loadingUser, userLoading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              logUserActivity(
                "click",
                "back_button",
                "forum_topic",
                "navigate_back"
              );
              navigate("/forum");

            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Forum</h1>
          <div className="h-6 w-6" />
        </div>

        <div className="flex items-center justify-center mt-20">
          <div className="text-center">
            <p className="text-black mb-4">Kamu belum memilih preferensi</p>
            <button
              onClick={() => navigate("/edit-profile")}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg"
            >
              Pilih preferensi
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white px-4 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => {
            logUserActivity(
              "navigation",
              "go_back_from_forum_categories",
              "ForumCategory",
              "User clicked back from forum categories"
            );
            navigate("/");
          }}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-semibold">Forum</h1>
        <div className="h-6 w-6" /> {/* spacer */}
      </div>

      {/* Grid Categories */}
      <div className="grid grid-cols-2 gap-4">
        {categories.map((cat) => (
          <div key={cat.id} className="flex justify-center">
            <button
              className={`${cat.bg} rounded-3xl p-4 h-40 w-40 flex flex-col justify-between items-start relative`}
              onClick={() => navigate(`/forum/${cat.id}`)}
            >
              <p className="text-md text-left font-semibold text-gray-800 leading-tight">
                {cat.title}
              </p>
              <img
                src="/icon-arrow-up-right.png"
                alt="Arrow"
                className="h-8 w-8 absolute bottom-2 right-2"
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
